import { useBoolean } from '@uifabric/react-hooks';
import { useEvent } from 'kits/eventKit';
import { useEffect, useState } from 'react';
import { useIsSideBarMaximized, useWindowWidth } from 'shared-state/display-options/hooks';
import {
    useIdentityClaims,
    useUserStatus,
    useAtWorkLocation,
    useAtWorkCustomPhrase,
    useShowAwayExtensionModal,
    useAwaySinceTimestamp
} from 'shared-state/identity/hooks';
import { IIdentityClaims } from 'shared-state/identity/types';
import { useCurrentAreaId, useCurrentRoomId } from 'shared-state/location/hook';
import { consumeLobbyPreserveStatus } from 'features/Rooms/RoomList/helpers/lobbyRedirect';

export interface IMainState {
    identityClaims: IIdentityClaims | undefined;
    currentRoomId: string | undefined;
    currentAreaId: string | undefined;
    onTabSelect: () => void;
    smallDevice: boolean;
    mobileMenuOpen: boolean;
    toggleAlert: () => void;
    displayAlert: boolean;
}

export const useMainState: () => IMainState = () => {
    const [identityClaims] = useIdentityClaims();

    const [currentRoomId] = useCurrentRoomId();
    const [currentAreaId] = useCurrentAreaId();
    const [, setCurrentStatus] = useUserStatus();
    const [, setAtWorkLocation] = useAtWorkLocation();
    const [, setAtWorkCustomPhrase] = useAtWorkCustomPhrase();
    const [, setShowAwayExtensionModal] = useShowAwayExtensionModal();
    const [, setAwaySinceTimestamp] = useAwaySinceTimestamp();

    const [, setSideBarIsMaximized] = useIsSideBarMaximized();

    const [displayAlert, { toggle: toggleAlert }] = useBoolean(false);

    const onTabSelect = () => setSideBarIsMaximized(true);

    useEffect(() => {
        if (!('Notification' in window)) {
            toggleAlert();
        } else {
            Notification.requestPermission();
        }
    }, []);

    useEffect(() => {
        if (!currentAreaId) return;
        const preserveStatus = consumeLobbyPreserveStatus();
        if (preserveStatus === null) return;
        if (!preserveStatus) {
            setCurrentStatus('available');
            setAtWorkLocation('office');
            setAtWorkCustomPhrase(undefined);
        }
    }, [currentAreaId, setCurrentStatus, setAtWorkLocation, setAtWorkCustomPhrase]);

    useEffect(() => {
        if (!currentRoomId) return;
        if (localStorage.getItem('isUserSelectedAwayManually') !== 'true') return;
        localStorage.removeItem('isUserSelectedAwayManually');
        setAwaySinceTimestamp(undefined);
        setShowAwayExtensionModal(false);
        setCurrentStatus('available');
        setAtWorkLocation('office');
        setAtWorkCustomPhrase(undefined);
    }, [currentRoomId, setAwaySinceTimestamp, setShowAwayExtensionModal, setCurrentStatus, setAtWorkLocation, setAtWorkCustomPhrase]);

    const { smallDevice } = useWindowWidth();

    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    useEvent('closeMobileRoomsPanel', () => {
        setMobileMenuOpen(false);
    });
    useEvent('openMobileRoomsPanel', () => {
        setMobileMenuOpen(true);
    });

    return { identityClaims, currentRoomId, currentAreaId, onTabSelect, smallDevice, mobileMenuOpen, displayAlert, toggleAlert };
};
